import {
  bannerTemaAMedias, bannerTemaFallido, bannerTemaListo,
} from './curacionCopys';

/* PIPE-INST T5 (firma #122) — EL DIFF QUE PRODUCE LOS AVISOS MOMENTÁNEOS.
 *
 * ── QUÉ SUSTITUYE, Y POR QUÉ HACÍA FALTA SUSTITUIRLO ────────────────────────
 *
 * Hasta T4.5 el final de una generación lo contaba un LETRERO PERSISTENTE que
 * abría la pantalla: «Generación terminada: 2/4 temas listos para revisar.». Lo
 * armaba el servidor mirando el último paquete cerrado, y por eso seguía ahí al
 * día siguiente, describiendo una generación de ayer como si acabara de pasar —
 * y con `aria-live` puesto, cantándola. Un aviso que no caduca deja de ser un
 * aviso: se convierte en decoración que el profesor aprende a ignorar.
 *
 * ── EL MODELO NUEVO: LA TRANSICIÓN, NO EL ESTADO ────────────────────────────
 *
 * Un banner anuncia un CAMBIO que acaba de ocurrir delante del profesor, así que
 * no se puede derivar de la foto: hay que comparar la foto ANTERIOR del sondeo
 * con la nueva. Sale un aviso por cada tema que pasó de estar corriendo
 * (En fila / Generando…) a un estado final.
 *
 * QUIEN LLEGA TARDE NO VE NADA, Y ESTÁ BIEN. Un profesor que entra cuando todo
 * ya terminó no recibe ningún banner — y no lo necesita: los chips por tema ya
 * cuentan la historia entera, sin caducar y sin mentir. Ésa es exactamente la
 * diferencia con el letrero que murió.
 *
 * ── LA LECCIÓN DE T4.5 QUE ESTE ARCHIVO TIENE QUE RESPETAR ──────────────────
 *
 * «La membresía de un paquete se decide por TRABAJOS, no por la foto vigente.»
 * El defecto de T4.5 fue contar temas por su `paquete_id` VIGENTE, que migra
 * cuando el profesor relanza un tema — y el letrero encogía retroactivamente.
 * Aquí eso se evita de raíz porque el diff es POR TEMA Y POR CORRIDA: la clave
 * de un aviso es `tema_id + paquete_id`, o sea la corrida concreta que terminó.
 * Dos corridas del mismo tema son dos avisos, y la misma corrida vista dos veces
 * es UNO — que es lo que hace que la cola no repita.
 */

/** Los dos estados que significan «esto se está moviendo». */
export const ACTIVOS = ['en_fila', 'generando'];

/** Los tres finales que un tema puede alcanzar saliendo de una corrida.
 *  `revisado` está aquí porque un tema ya curado al que se le generan preguntas
 *  nuevas vuelve a «listo_para_revisar»… salvo que el profesor las cure más
 *  rápido que el sondeo, en cuyo caso el estado que se observa es `revisado`. El
 *  tema terminó igual, y el aviso es igual de cierto. */
export const FINALES = ['listo_para_revisar', 'revisado', 'no_se_pudo'];

/** La identidad de UNA corrida de UN tema. Lo que hace que el mismo final no se
 *  anuncie dos veces y que dos corridas distintas sí sean dos avisos. */
export function claveDeAviso(tema) {
  return `${tema?.tema_id}:${tema?.paquete_id || ''}`;
}

/**
 * Los avisos que hay que sacar entre dos fotos del sondeo.
 *
 * TANDA 26 (firma #234): recibe `t` como PRIMER argumento, igual que el resto
 * del carril. Es una función PURA, no un componente, así que no puede llamar a
 * `useTranslation` — se lo pasa quien la llama, que es `GroupBankTemasPage`.
 * Los MAPAS DE ESTADO de este archivo (`ACTIVOS`, `FINALES`) no se tocan: son
 * códigos que viajan por la red, no texto.
 *
 * @param {Function} t           el `t` de i18next de la pantalla.
 * @param {Array|null} anterior  los temas de la respuesta previa (null en la
 *                               primera carga: sin foto previa no hay transición
 *                               que detectar, y por tanto no hay aviso).
 * @param {Array} nuevo          los temas de la respuesta recién llegada.
 * @returns {Array<{clave: string, texto: string, tono: string}>}
 */
export function avisosDeTransicion(t, anterior, nuevo) {
  if (!Array.isArray(anterior) || !Array.isArray(nuevo)) return [];
  // El parametro del callback se llama `prev` y NO `t`: desde la TANDA 26 `t`
  // es el traductor de la funcion, y dejarlo sombreado aqui seria una trampa
  // esperando a la proxima linea que quiera traducir dentro de este map.
  const antes = new Map(anterior.map(prev => [prev.tema_id, prev]));
  const salida = [];
  for (const tema of nuevo) {
    const previo = antes.get(tema.tema_id);
    // SIN FOTO PREVIA DE ESTE TEMA NO HAY AVISO. Es el caso de un tema que
    // aparece por primera vez (otra pestaña generó, el mapa cambió): no vimos su
    // transición, así que no la contamos como si la hubiéramos visto.
    if (!previo) continue;
    if (!ACTIVOS.includes(previo.estado)) continue;
    if (!FINALES.includes(tema.estado)) continue;

    // EL CONTEO QUE VIAJA EN EL COPY ES EL DE «POR REVISAR», no el total del
    // tema: es lo que el profesor tiene delante para hacer, y es lo que el
    // banner le está pidiendo que haga. Un tema con 30 preguntas de las que ya
    // curó 25 tiene 5 «para revisar», y decirle 30 sería mandarlo a una cola que
    // no existe.
    const porRevisar = tema.pendientes || 0;
    const nombre = tema.nombre || '';
    if (tema.estado === 'no_se_pudo') {
      salida.push({ clave: claveDeAviso(tema), tono: 'error',
        texto: bannerTemaFallido(t, nombre) });
    } else if (tema.a_medias) {
      salida.push({ clave: claveDeAviso(tema), tono: 'aviso',
        texto: bannerTemaAMedias(t, nombre, porRevisar) });
    } else {
      salida.push({ clave: claveDeAviso(tema), tono: 'exito',
        texto: bannerTemaListo(t, nombre, porRevisar) });
    }
  }
  return salida;
}
